import React, { useEffect, useState } from "react";
import clsx from "clsx";
import { useIntl } from "react-intl";

import { ModalComponent } from "@app/core/modal";
import { DeleteModal } from "@app/modules/delete-modal/delete-modal.component";
import {
  GetAllProductsExtendedSortByEnum,
  GetAllProductsExtendedSortOrderEnum,
  OutAdminProductDTO,
  OutAdminProductDTOCreatedByEnum,
  PlatformDTO
} from "@app/api/generated";
import { LabelComponent } from "@app/core";
import { useAppDispatch, useAppSelector } from "@app/redux/store";
import { Column, ISortDirection } from "@webbio/components";
import { Table } from "@app/components/table/table";
import { ROUTES } from "@app/constants/routes";
import { platformTranslationKeys } from "@app/constants/platform";
import { productsThunk } from "@app/redux/thunks/products.thunk";
import { useDateFnsFormat } from "@app/util/use-date-fns-format";
import { useUrlListState } from "@app/util/use-url-list-state";
import { getLifecycleWarning, getProductStatus } from "@app/util/product-lifecycle";
import { TableActions } from "@app/core/table-actions";
import { Overview } from "@app/components/overview/overview";
import { useOnButtonLinkClick } from "@app/util/use-on-button-link-click";

import { ProductFilterBar } from "./product-filter-bar/product-filter-bar.component";

const TAKE = 30;

const ProductOverview = () => {
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const {
    page: currentPage,
    filters,
    setPage,
    setFilters
  } = useUrlListState(["search", "status", "platform", "sort", "order"] as const);
  const { products, isLoading, totalPages, totalResults } = useAppSelector((state) => state.products);

  const { formatDate } = useDateFnsFormat();
  const { onButtonLinkClick } = useOnButtonLinkClick();
  const [isDeleteFilterOpen, setIsDeleteFilterOpen] = useState<boolean>(false);
  const [filterToDelete, setFilterToDelete] = useState<OutAdminProductDTO | undefined>(undefined);

  // URL is the source of truth for page + filters + sort (so back-navigation restores the exact view).
  const search = filters.search || undefined;
  const statusParam = Number(filters.status);
  const status = statusParam === 1 || statusParam === 2 ? statusParam : undefined;
  const platformParam = Number(filters.platform);
  const platform = Number.isInteger(platformParam) && platformParam > 0 ? platformParam : undefined;
  const sortBy = filters.sort === "noDealsSince" ? GetAllProductsExtendedSortByEnum.noDealsSince : undefined;
  const sortOrder =
    filters.order === "ASC"
      ? GetAllProductsExtendedSortOrderEnum.ASC
      : filters.order === "DESC"
      ? GetAllProductsExtendedSortOrderEnum.DESC
      : undefined;

  useEffect(() => {
    const skip = (currentPage - 1) * TAKE;
    dispatch(productsThunk.getProducts(skip, TAKE, platform, search, status, sortBy, sortOrder));
    window.scrollTo(0, 0);
  }, [dispatch, currentPage, search, status, platform, sortBy, sortOrder]);

  const handleSort = (columnId?: string, sortDirection?: ISortDirection) => {
    if (columnId !== "noDealsSince") {
      return;
    }
    setFilters({ sort: sortDirection ? "noDealsSince" : undefined, order: sortDirection });
  };

  const onDeleteProduct = (product: OutAdminProductDTO) => {
    setFilterToDelete(product);
    setIsDeleteFilterOpen(true);
  };

  const onDeleteConfirmed = () => {
    if (filterToDelete) {
      dispatch(productsThunk.deleteProduct(filterToDelete.id));
      setIsDeleteFilterOpen(false);
    }
  };

  const onPageChange = (page: number) => setPage(page);

  const onTableRowAuxClick = (row: OutAdminProductDTO, e: React.MouseEvent<HTMLTableCellElement, MouseEvent>) =>
    onTableRowClick(row, e, true);

  const onTableRowClick = (
    row: OutAdminProductDTO,
    e: React.MouseEvent<HTMLTableCellElement, MouseEvent>,
    forceTargetBlank?: boolean
  ) => {
    onButtonLinkClick(`${ROUTES.PRODUCTS}/${row.id}`, e, forceTargetBlank);
  };

  return (
    <Overview
      title={intl.formatMessage({ id: "products.overview.title" }, { total: intl.formatNumber(totalResults) })}
      actionBar={<ProductFilterBar />}
      isLoading={isLoading}
      currentPage={currentPage}
      totalPages={totalPages}
      onPageChange={onPageChange}
      overviewItems={products}
    >
      {products && products.length > 0 && (
        <Table
          dataSource={products || []}
          isLoading={isLoading}
          onSort={handleSort}
          defaultSort={{ column: "noDealsSince", direction: sortBy ? sortOrder : undefined }}
        >
          <Column
            title={intl.formatMessage({ id: "products.overview.table.th.status" })}
            field="live"
            id="status"
            isNotSortable
            onTdClick={onTableRowClick}
            onTdAuxClick={onTableRowAuxClick}
            maxWidth={100}
            width={100}
            cell={(_live, _index, product: OutAdminProductDTO) => {
              const productStatus = getProductStatus(product);
              return (
                <LabelComponent type={productStatus.type} label={intl.formatMessage({ id: productStatus.labelId })} />
              );
            }}
          />
          <Column
            title={intl.formatMessage({ id: "products.overview.table.th.title" })}
            field="title"
            id="title"
            isNotSortable
            onTdClick={onTableRowClick}
            onTdAuxClick={onTableRowAuxClick}
            cell={(title) => (
              <span className="truncate-table" title={title}>
                {title || "-"}
              </span>
            )}
          />
          <Column
            title={intl.formatMessage({ id: "products.overview.table.th.ean" })}
            field="ean"
            id="ean"
            isNotSortable
            maxWidth={130}
            width={130}
            onTdClick={onTableRowClick}
            onTdAuxClick={onTableRowAuxClick}
            cell={(ean?: string) => (
              <span className="truncate-table" title={ean}>
                {ean || "—"}
              </span>
            )}
          />
          <Column
            title={intl.formatMessage({ id: "products.overview.table.th.platform" })}
            field="platform"
            id="platform"
            isNotSortable
            maxWidth={90}
            width={90}
            onTdClick={onTableRowClick}
            onTdAuxClick={onTableRowAuxClick}
            cell={(platformValue?: PlatformDTO) => {
              const translationKey = platformTranslationKeys.find((item) => item.id === platformValue?.id);
              const platformString = translationKey ? intl.formatMessage({ id: translationKey.name }) : "-";

              return (
                <span className="truncate-table" title={platformString}>
                  {platformString}
                </span>
              );
            }}
          />
          <Column
            title={intl.formatMessage({ id: "products.overview.table.th.createdBy" })}
            field="createdBy"
            id="createdBy"
            isNotSortable
            maxWidth={90}
            width={90}
            onTdClick={onTableRowClick}
            onTdAuxClick={onTableRowAuxClick}
            cell={(createdBy: OutAdminProductDTOCreatedByEnum) => (
              <>
                {intl.formatMessage({
                  id:
                    createdBy === OutAdminProductDTOCreatedByEnum.API
                      ? "products.overview.addedBy.sync"
                      : "products.overview.addedBy.manual"
                })}
              </>
            )}
          />
          <Column
            title={intl.formatMessage({ id: "products.overview.table.th.noDealsSince" })}
            field="noDealsSince"
            id="noDealsSince"
            sortField="noDealsSince"
            maxWidth={140}
            width={140}
            onTdClick={onTableRowClick}
            onTdAuxClick={onTableRowAuxClick}
            cell={(noDealsSince: Date | null | undefined, _index: number, product: OutAdminProductDTO) => {
              const warning = getLifecycleWarning(product);

              return (
                <span
                  style={{ display: "inline-flex", alignItems: "center", gap: 8 }}
                  title={noDealsSince ? formatDate(new Date(noDealsSince), "dd-MM-yyyy") : "—"}
                >
                  {warning ? (
                    <LabelComponent type={warning.type} label={intl.formatMessage({ id: warning.labelId })} />
                  ) : (
                    <span className="table-text-faded">
                      {noDealsSince ? formatDate(new Date(noDealsSince), "dd-MM-yyyy") : "—"}
                    </span>
                  )}
                </span>
              );
            }}
          />
          <Column
            title={intl.formatMessage({ id: "products.overview.table.th.updatedAt" })}
            field="updatedAt"
            id="updatedAt"
            isNotSortable
            onTdClick={onTableRowClick}
            onTdAuxClick={onTableRowAuxClick}
            maxWidth={140}
            width={140}
            cell={(updatedAt) => (
              <span className={clsx("truncate-table", "table-text-faded")}>
                {updatedAt ? formatDate(updatedAt, "eeeeee. dd MMM yyyy HH:mm") : ""}
              </span>
            )}
          />
          <Column
            title=""
            field="id"
            isNotSortable
            cell={(_, __, product: OutAdminProductDTO) => (
              <TableActions editLink={`${ROUTES.PRODUCTS}/${product.id}`} onDelete={() => onDeleteProduct(product)} />
            )}
          />
        </Table>
      )}
      <ModalComponent
        title={intl.formatMessage({ id: "products.overview.modal.delete.title" })}
        isModalOpen={isDeleteFilterOpen}
        onCloseModal={() => setIsDeleteFilterOpen(false)}
        variant="big"
      >
        <DeleteModal onCancel={() => setIsDeleteFilterOpen(false)} onDelete={onDeleteConfirmed} />
      </ModalComponent>
    </Overview>
  );
};

export { ProductOverview };
